import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Input,
  InputProps,
  Button,
  FormField,
  SpaceBetween,
  Form,
  Container,
  ColumnLayout,
} from "@cloudscape-design/components-themed";
import { LABELS } from "./constants";
import { COMMON_LABELS } from "../default-constants";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  setSearchCondition,
  INITIAL_SEARCH_CRITERIA,
} from "../../features/slices/bank-accounts-list";
import {
  clearAllErrTxts,
  clearFormFieldErrTxt,
  createFormFieldErrTxts,
} from "../../features/error/validation-slice";
import {
  getErrorText,
  isLengthValid,
  isHalfNumberValid,
} from "../../utils/inputValidation";
import { BankAccountsListSearchRequest } from "../../features/api-interfaces";
import { useLocation } from "react-router-dom";

interface SearchBarProps {
  isDocomoUser: boolean;
  setIsSkipSearch: Dispatch<SetStateAction<boolean>>;
}

const SearchBar = (props: SearchBarProps) => {
  const { isDocomoUser, setIsSkipSearch } = props;
  const [searchInput, setSearchInput] = useState(INITIAL_SEARCH_CRITERIA);

  const [errText, setErrText] = useState<string>();

  const dispatch = useAppDispatch();

  // URL取得
  const pathname = useLocation().pathname;

  // 検索条件チェック用
  const [searchInvalid, setSearchInvalid] = useState(false);

  const { formFieldErrTxts } = useAppSelector((state) => state.validation);
  // * 検索条件ステート更新関数
  const handleSearchInputOnChange = (
    name: keyof BankAccountsListSearchRequest,
    value: string
  ) => {
    switch (name) {
      case "ipid":
        if (!isHalfNumberValid(value)) {
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR006", msgArgs: [] })
          );
          setSearchInvalid(true);
          break;
        } else if (!isLengthValid(value, 11)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR059",
              msgArgs: ["11"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "applicantCompanyName":
      case "referenceGroupName":
        if (!isLengthValid(value, 30)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["30"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
      case "merchantName":
        if (!isLengthValid(value, 15)) {
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR058",
              msgArgs: ["15"],
            })
          );
          setSearchInvalid(true);
          break;
        } else {
          dispatch(clearFormFieldErrTxt(name));
          break;
        }
    }
    setSearchInput({ ...searchInput, [name]: value });
  };

  // 画面遷移する際にエラーメッセージの初期化
  useEffect(() => {
    dispatch(clearAllErrTxts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // * 検索ボタンイベント
  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    //バリデーションチェック
    setSearchInvalid(false);

    handleSearchInputOnChange(
      "applicantCompanyName",
      searchInput.applicantCompanyName
    );
    handleSearchInputOnChange(
      "referenceGroupName",
      searchInput.referenceGroupName
    );
    handleSearchInputOnChange("ipid", searchInput.ipid);
    handleSearchInputOnChange("merchantName", searchInput.merchantName);
    if (searchInvalid) return;

    setIsSkipSearch(false);
    dispatch(
      setSearchCondition({ ...searchInput, timestamp: new Date().getTime() })
    );
    setErrText("");
  };

  return (
    <form onSubmit={handleOnSubmit}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal">{COMMON_LABELS.BTNS.SEARCH}</Button>
          </SpaceBetween>
        }
        errorText={errText}
      >
        <Container>
          <SpaceBetween direction="vertical" size="xxl">
            <SpaceBetween direction="vertical" size="xxxs">
              <ColumnLayout columns={4}>
                {isDocomoUser ? (
                  <FormField
                    label={LABELS.APPLICANT_COMPANY_NAME}
                    errorText={getErrorText(
                      "applicantCompanyName",
                      formFieldErrTxts
                    )}
                    constraintText="30文字以内"
                  >
                    <Input
                      name="applicantCompanyName"
                      value={searchInput.applicantCompanyName}
                      onChange={({
                        detail,
                      }: {
                        detail: InputProps.ChangeDetail;
                      }) =>
                        handleSearchInputOnChange(
                          "applicantCompanyName",
                          detail.value
                        )
                      }
                    />
                  </FormField>
                ) : null}

                <FormField
                  label={LABELS.REFERENCE_GROUP_NAME}
                  errorText={getErrorText(
                    "referenceGroupName",
                    formFieldErrTxts
                  )}
                  constraintText="30文字以内"
                >
                  <Input
                    name="referenceGroupName"
                    value={searchInput.referenceGroupName}
                    onChange={({
                      detail,
                    }: {
                      detail: InputProps.ChangeDetail;
                    }) =>
                      handleSearchInputOnChange(
                        "referenceGroupName",
                        detail.value
                      )
                    }
                  />
                </FormField>
              </ColumnLayout>
              <ColumnLayout columns={4}>
                <FormField
                  label={LABELS.MERCHANTS_NAME}
                  errorText={getErrorText("merchantName", formFieldErrTxts)}
                  constraintText="15文字以内"
                >
                  <Input
                    name="merchantName"
                    value={searchInput.merchantName}
                    onChange={({
                      detail,
                    }: {
                      detail: InputProps.ChangeDetail;
                    }) =>
                      handleSearchInputOnChange("merchantName", detail.value)
                    }
                  />
                </FormField>
                <FormField
                  label={LABELS.IPID}
                  errorText={getErrorText("ipid", formFieldErrTxts)}
                  constraintText="半角数字11桁以内"
                >
                  <Input
                    name="ipid"
                    value={searchInput.ipid}
                    onChange={({
                      detail,
                    }: {
                      detail: InputProps.ChangeDetail;
                    }) => handleSearchInputOnChange("ipid", detail.value)}
                  />
                </FormField>
              </ColumnLayout>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      </Form>
    </form>
  );
};

export default SearchBar;
